<script setup lang="ts">
import { ref, type Ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { myRequest } from '@/utils/fetch'
import OGHeader from '@/components/OGHeader.vue'
import { useQuasar } from 'quasar'
import PersonProfile from '@/components/PersonProfile.vue'
import type { PersonResponse } from '@/types/personProfile'
import type { crunchbaseData } from '@/types/company'
import UpdatePersonDialog from '@/components/UpdatePersonDialog.vue'
import { useAuthStore } from '@/stores/authStore'
import { useAppStore } from '@/stores/appStore'

type WeatherData = {
  temperature: {
    f: {
      max: number
      min: number
      current: number
    }
    c: {
      max: number
      min: number
      current: number
    }
  }
  condition: string
  icon: string
  humidity: number
  windSpeed: {
    f: number
    c: number
  }
  chanceOfRain: number
  chanceOfSnow: number
}

const route = useRoute()
type Response = (PersonResponse & {
  Company: {
    companyID: number
    name: string
    website: string
    description: string
    news: Array<{
      title: string
      link: string
      source: string
      datetime: string
    }>
    newsSync: Date
    crunchbase?: crunchbaseData
  }
}) & {
  weather: WeatherData
}

const loading = ref(false)
const person: Ref<Response | null> = ref(null)
const ready = ref(false)
const $q = useQuasar()
const shareLink = ref('')
const showUpdateDialog = ref(false)
const authStore = useAuthStore()
const appStore = useAppStore()

const displayName = computed(() => {
  if (person.value?.firstName !== '') {
    return `${person.value?.firstName} ${person.value?.lastName}`
  }
  if (person.value?.enrichmentData?.firstName && person.value?.enrichmentData?.firstName !== '') {
    return `${person.value.enrichmentData.firstName} ${person.value.enrichmentData.lastName}`
  }
  if (person.value?.enrichmentData?.fullName && person.value?.enrichmentData?.fullName !== '') {
    return person.value.enrichmentData.fullName
  }
  return person.value?.email || ''
})

const handleShare = async () => {
  try {
    const response = await myRequest('POST', `/app/event/${route.params.eventID}/person/${route.params.personID}/share`)
    shareLink.value = `${window.location.origin}/shared/${response.sharedProfileID}`

    if (navigator.share) {
      await navigator.share({
        title: `${displayName.value}'s Profile`,
        text: `Check out ${displayName.value}'s profile`,
        url: shareLink.value
      })
    } else {
      $q.dialog({
        title: 'Share Profile',
        message: `Copy this link to share: ${shareLink.value}`,
        ok: {
          label: 'Copy',
          color: 'primary'
        },
        cancel: true
      }).onOk(() => {
        navigator.clipboard.writeText(shareLink.value)
        $q.notify({
          message: 'Link copied to clipboard',
          color: 'positive'
        })
      })
    }
  } catch (error) {
    $q.notify({
      message: 'Failed to share profile',
      color: 'negative'
    })
  }
}

const handleEdit = () => {
  showUpdateDialog.value = true
}

const handleToggleSections = () => {
  appStore.updateShowAllSections(!appStore.showAllSections)
}

const fetchData = async () => {
  loading.value = true
  const result = (await myRequest(
    'GET',
    `/app/event/${route.params.eventID}/person/${route.params.personID}`
  )) as Response
  person.value = result
  ready.value = true
  loading.value = false
}

fetchData()
</script>

<template>
  <OGHeader :title="displayName" :subtitle="person?.email || ''">
    <template v-slot:title-actions>
      <q-btn flat round dense icon="mdi-pencil" @click="handleEdit" />
    </template>
    <template v-slot:actions>
      <q-btn
        flat
        round
        dense
        :icon="appStore.showAllSections ? 'mdi-eye-off' : 'mdi-eye'"
        @click="handleToggleSections"
        class="q-mr-sm"
        color="primary"
        size="md"
      >
        <q-tooltip>{{ appStore.showAllSections ? 'Hide Filtered Sections' : 'Show All Sections' }}</q-tooltip>
      </q-btn>
      <q-btn
        flat
        round
        dense
        icon="mdi-share-variant"
        @click="handleShare"
        :loading="loading"
        class="q-mr-sm"
        color="primary"
        size="md"
      >
        <q-tooltip>Share Profile</q-tooltip>
      </q-btn>
    </template>
  </OGHeader>
  <PersonProfile
    v-if="person"
    :person="person"
    :ready="ready"
    :show-generate="true"
    :show-all-sections="appStore.showAllSections"
    :enrichment-filters="authStore.settings?.enrichmentFilters"
  />
  <q-inner-loading :showing="loading">
    <q-spinner-gears size="50px" color="primary" />
  </q-inner-loading>

  <UpdatePersonDialog
    v-model="showUpdateDialog"
    :person="{
      personID: Number(route.params.personID),
      firstName: person?.firstName || '',
      lastName: person?.lastName || '',
      email: person?.email || ''
    }"
    :eventID="Number(route.params.eventID)"
    @search-complete="fetchData"
  />
</template>
